import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDate, fmtMoney, formatMarketValue } from "../lib/format";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";

export default function ScoutingPdf() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get(`/clients/${id}`).then((r) => setClient(r.data));
    api.get(`/clients/${id}/stats`).then((r) => setStats(r.data));
  }, [id]);
  useEffect(() => {
    // Auto-trigger print dialog when data is ready
    if (client && stats) {
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [client, stats]);
  if (!client || !stats) return <div className="p-10 text-slate-500">Generating scouting PDF…</div>;
  const last = stats.seasons[stats.seasons.length - 1];
  return (
    <div className="min-h-screen bg-white p-10 font-sans" data-testid="scouting-pdf">
      <style>{`@media print { body { -webkit-print-color-adjust: exact; } }`}</style>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between border-b-4 border-emerald-500 pb-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-emerald-700">Playmaker Agency · Scouting Brief</div>
            <h1 className="font-display font-black text-4xl tracking-tight">{client.full_name}</h1>
            <div className="text-slate-600 mt-1">{client.position} · {client.current_club} · {client.nationality_flag} {client.nationality}</div>
          </div>
          <div className="text-right">
            <img src={client.photo_url} alt="" className="w-24 h-32 object-cover rounded-lg ring-1 ring-slate-200" />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          <Box label="DOB" value={fmtDate(client.date_of_birth)} />
          <Box label="Market value" value={formatMarketValue(client.market_value_eur)} />
          <Box label="Weekly wage" value={fmtMoney(client.weekly_wage, client.currency)} />
          <Box label="Contract end" value={fmtDate(client.contract_end)} />
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <Section>Last season ({last?.season})</Section>
            <div className="grid grid-cols-3 gap-2">
              <Box label="Apps" value={last?.appearances} />
              <Box label="Goals" value={last?.goals} />
              <Box label="Assists" value={last?.assists} />
              <Box label="Key passes/90" value={last?.key_passes} />
              <Box label="Prog. carries/90" value={last?.progressive_carries} />
              <Box label="Duels won %" value={`${last?.duels_won_pct}%`} />
            </div>
          </div>
          <div>
            <Section>SciSports profile</Section>
            <div className="h-48">
              <ResponsiveContainer><RadarChart data={stats.radar} outerRadius="75%">
                <PolarGrid stroke="#cbd5e1" />
                <PolarAngleAxis dataKey="axis" tick={{ fontSize: 9, fill: "#475569" }} />
                <PolarRadiusAxis tick={false} axisLine={false} />
                <Radar dataKey="value" stroke="#00C863" fill="#00E676" fillOpacity={0.35} />
              </RadarChart></ResponsiveContainer>
            </div>
            <div className="text-xs text-slate-600 mt-1">Wyscout rating <b>{stats.wyscout_rating}</b> · SciSports <b>{stats.scisports_percentile}th pct</b></div>
          </div>
        </div>

        <Section>Market value timeline</Section>
        <div className="h-48 mb-6">
          <ResponsiveContainer><LineChart data={stats.market_history}>
            <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#64748B" }} tickFormatter={(v) => v?.slice(0, 7)} />
            <YAxis tick={{ fontSize: 9, fill: "#64748B" }} unit="M" />
            <Tooltip formatter={(v) => `€${v}M`} />
            <Line type="monotone" dataKey="value" stroke="#0A1128" strokeWidth={2.5} dot={{ fill: "#00E676", r: 3 }} />
          </LineChart></ResponsiveContainer>
        </div>

        {client.bio && (<>
          <Section>Scout-eye view</Section>
          <p className="text-sm text-slate-700 leading-relaxed">{client.bio}</p>
        </>)}

        <div className="mt-10 pt-4 border-t border-slate-200 text-[10px] text-slate-500 flex items-center justify-between">
          <div>Generated {new Date().toLocaleString("en-GB")}</div>
          <div>Confidential · Playmaker Agency</div>
        </div>
      </div>
    </div>
  );
}

function Box({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
      <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className="font-mono-tab font-bold text-base mt-0.5">{value}</div>
    </div>
  );
}
function Section({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700 mb-2">{children}</div>;
}
